import React, { useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { ButtonItem } from '@atlaskit/menu';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import Modal, { ModalBody, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { DisplayLabel } from '@atlassian/jira-servicedesk-language-settings-translations-resource/src/services/get-data/types.tsx';
import type { PreviewSendTestEmailActionsProps } from '../../common/types/preview-send-test-email-actions';
import messages from './messages';

export type Category = string;

export const PreviewSendTestEmailActions = ({
	defaultLanguage,
	selectedLanguage,
	translationPanel,
	projectKey,
	ContentPreview,
	SendMeATestEmail,
	hasMultipleActions,
}: PreviewSendTestEmailActionsProps) => {
	const { translationFields } = translationPanel.translationGroups[0];

	const findContent = (category: Category, isDefault: boolean): DisplayLabel =>
		translationFields.find((translationField) => translationField.category === category)
			?.translationContentMap[isDefault ? defaultLanguage.key : selectedLanguage.key] ?? '';

	const contentTemplate = findContent('sd.notifications.custom.email.content', false);
	const ruleSetId = translationPanel.entityId;

	const { formatMessage } = useIntl();
	const [isPreviewModal, setIsPreviewModal] = useState(false);
	const onPreviewLabelClick = () => {
		setIsPreviewModal(true);
	};
	const closePreviewModal = () => {
		setIsPreviewModal(false);
	};

	const content = contentTemplate.replace(/\n/g, '').replace(/ /g, '');
	const isDisablePreviewButton = content === '';

	if (hasMultipleActions) {
		return (
			<ActionsDropDownWrapper>
				<DropdownMenu
					testId="language-settings.ui.preview-send-test-email-actions.dropdown-menu"
					trigger={({ triggerRef, ...dropdownProps }) => (
						<Button
							{...dropdownProps}
							appearance="subtle"
							iconBefore={<MoreIcon label={formatMessage(messages.menuLabel)} />}
							ref={triggerRef}
						/>
					)}
				>
					<DropdownItemGroup>
						<ActionItemsWrapper>
							<PreviewItemWrapper>
								<ButtonItem onClick={onPreviewLabelClick} isDisabled={isDisablePreviewButton}>
									{formatMessage(messages.previewLabel)}
								</ButtonItem>
							</PreviewItemWrapper>
							<SendMeATestEmail
								format="html"
								projectKey={projectKey}
								initialData={{
									languageTag: selectedLanguage.key,
									contexts: [],
								}}
								stylingData={{
									textTemplate: contentTemplate,
								}}
								ruleSetId={ruleSetId}
								hasMultipleActions
							/>
						</ActionItemsWrapper>
					</DropdownItemGroup>
				</DropdownMenu>
				{isPreviewModal && (
					<ModalTransition>
						<Modal onClose={closePreviewModal}>
							<ModalHeader>
								<ModalTitle>{formatMessage(messages.previewModalTitle)}</ModalTitle>
							</ModalHeader>
							<ModalBody>
								<ContentPreview
									content={{
										textTemplate: contentTemplate,
									}}
									initialData={{
										contexts: [],
										languageTag: selectedLanguage.key,
										format: 'html',
									}}
									projectKey={projectKey}
									ruleSetId={ruleSetId}
								/>
							</ModalBody>
						</Modal>
					</ModalTransition>
				)}
			</ActionsDropDownWrapper>
		);
	}

	return (
		<ActionsWrapper>
			<SendTestEmailButtonWrapper>
				<SendMeATestEmail
					format="html"
					projectKey={projectKey}
					initialData={{
						languageTag: selectedLanguage.key,
						contexts: [],
					}}
					stylingData={{
						textTemplate: contentTemplate,
					}}
					ruleSetId={ruleSetId}
					shouldShowSendTestEmailButton
				/>
			</SendTestEmailButtonWrapper>
			<PreviewButtonWrapper>
				<Button onClick={onPreviewLabelClick} isDisabled={isDisablePreviewButton}>
					{formatMessage(messages.previewLabel)}
				</Button>
			</PreviewButtonWrapper>
			{isPreviewModal && (
				<ModalTransition>
					<Modal onClose={closePreviewModal}>
						<ModalHeader>
							<ModalTitle>{formatMessage(messages.previewModalTitle)}</ModalTitle>
						</ModalHeader>
						<ModalBody>
							<ContentPreview
								content={{
									textTemplate: contentTemplate,
								}}
								initialData={{
									contexts: [],
									languageTag: selectedLanguage.key,
									format: 'html',
								}}
								projectKey={projectKey}
								ruleSetId={ruleSetId}
							/>
						</ModalBody>
					</Modal>
				</ModalTransition>
			)}
		</ActionsWrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsDropDownWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > button': {
		marginRight: token('space.100', '8px'),
	},
	marginBottom: token('space.500', '40px'),
	right: 0,
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionItemsWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PreviewItemWrapper = styled.div({
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsWrapper = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > button': {
		marginRight: token('space.100', '8px'),
	},
	marginBottom: token('space.500', '40px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SendTestEmailButtonWrapper = styled.div({
	position: 'absolute',
	right: token('space.1000', '80px'),
	paddingRight: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PreviewButtonWrapper = styled.div({
	position: 'absolute',
	right: token('space.200', '16px'),
});
